import { useEffect, useMemo, useState, type PropsWithChildren } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { Spinner } from "@components/v2";
import { type TableState } from "@features/sales-v4/types";
import {
  UniversalBenchmarksTable,
  type BenchmarkIndicatorRow,
} from "@features/universal-benchmarks";
import { getStageFromMetricId } from "@features/universal-benchmarks/consumer/utils";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";
import { formatting, type SalesMetricRow } from "@north-beam/nb-common";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { useMetricsData, usePerformanceData } from "../hooks";
import { TooltipTable } from "../tooltip-table";

interface Props {
  row: SalesMetricRow;
  tableState: TableState;
  rowItemName: string;
  metric: SalesMetricV2;
  tooltipTableMetrics: {
    metricBlended: SalesMetricV2;
    metricFt: SalesMetricV2;
    metricRtn: SalesMetricV2;
  };
  benchmarkKpis: BenchmarkIndicatorRow[] | null;
}

export const Tooltip = ({
  row,
  tableState,
  rowItemName,
  metric,
  benchmarkKpis,
  tooltipTableMetrics,
  children,
}: PropsWithChildren<Props>) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <GenericTooltip
      noInfoCircle
      overlayClassName="p-0 rounded-lg border-none shadow-[0_4px_8px] shadow-v2-black/15%"
      arrowClassName="bg-v2-secondary border-none"
      placement="top"
      mouseEnterDelay={500}
      content={
        <Content
          row={row}
          tableState={tableState}
          rowItemName={rowItemName}
          metric={metric}
          tooltipTableMetrics={tooltipTableMetrics}
          benchmarkKpis={benchmarkKpis}
          onLoadingChange={setIsLoading}
        />
      }
    >
      <div
        className={clsx({
          "cursor-wait": isLoading,
          "cursor-default": !isLoading,
        })}
      >
        {children}
      </div>
    </GenericTooltip>
  );
};

interface ContentProps extends Props {
  onLoadingChange: (val: boolean) => void;
}

const Content = ({
  row,
  tableState,
  rowItemName,
  metric,
  tooltipTableMetrics,
  benchmarkKpis,
  onLoadingChange,
}: ContentProps) => {
  const { user } = useUser();
  const { isDemoMode } = usePrefs();

  const { metricBlended, metricFt, metricRtn } = tooltipTableMetrics;

  const { data: metricsData, loading: metricsDataLoading } = useMetricsData({
    metrics: [metricBlended, metricFt, metricRtn],
    row,
    tableState,
  });

  const { tableData: performanceTableData, loading: performanceDataLoading } =
    usePerformanceData({ row, tableState, metric });

  const metricsTableData = useMemo(() => {
    if (!metricsData) {
      return [];
    }

    const getFormattedValue = (metric: SalesMetricV2) => {
      return formatting.formatMetric(
        metric.getValue(metricsData.metrics),
        metric.format,
        {
          precision: "exact",
          code: user.currencies.currentCurrency,
        },
      );
    };

    return [
      {
        label: "All",
        value: getFormattedValue(metricBlended),
      },
      {
        label: "New",
        value: getFormattedValue(metricFt),
      },
      {
        label: "Returning",
        value: getFormattedValue(metricRtn),
      },
    ];
  }, [
    metricsData,
    metricBlended,
    metricFt,
    metricRtn,
    user.currencies.currentCurrency,
  ]);

  const metricBenchmarkKpis = useMemo(() => {
    return (benchmarkKpis || []).filter((e) =>
      [metricBlended.id, metricFt.id, metricRtn.id].includes(e.metricId),
    );
  }, [benchmarkKpis, metricBlended, metricFt, metricRtn]);

  useEffect(() => {
    onLoadingChange(metricsDataLoading || performanceDataLoading);
  }, [onLoadingChange, metricsDataLoading, performanceDataLoading]);

  if (performanceTableData?.[0]?.valueDetails === "100%") {
    performanceTableData[0].valueDetails = "";
  }

  return (
    <div className="p-4 bg-v2-secondary rounded-lg text-v2-primary min-w-[340px]">
      {metricBenchmarkKpis.length > 0 ? (
        <div className="mb-4">
          <div className="text-sm font-semibold mb-1.5">{metric.name}</div>
          <div className="text-2xs flex items-center gap-1">
            <span className="inline-block font-semibold max-w-[240px] truncate">
              {rowItemName}
            </span>
            <span>performance relative to benchmark.</span>
          </div>
        </div>
      ) : (
        <div className="text-sm font-semibold mb-4">
          {metric.name}:{" "}
          {isDemoMode &&
          tableState.selectedTab !== "breakdown" &&
          rowItemName !== "Grand Total"
            ? generateAnonBase64Hash({
                value: rowItemName,
                prefix: tableState.selectedTab,
                doAnon: isDemoMode,
              })
            : rowItemName}
        </div>
      )}
      {metricsDataLoading || performanceDataLoading ? (
        <div className="flex items-center justify-center h-[230px]">
          <Spinner className="text-v2-cosmic/400" />
        </div>
      ) : (
        <>
          <div className="mb-4">
            {metricBenchmarkKpis.length > 0 ? (
              <UniversalBenchmarksTable
                rows={metricBenchmarkKpis}
                preferredStage={getStageFromMetricId(metric.id)}
              />
            ) : (
              <TooltipTable rows={metricsTableData} />
            )}
          </div>
          <div className="text-2xs font-semibold mb-3">
            Delayed Performance Lift
          </div>
          <TooltipTable rows={performanceTableData} />
        </>
      )}
    </div>
  );
};
